import React, { useState } from 'react';
import { Send, LoaderCircle, CircleCheck, CircleAlert, Phone } from 'lucide-react';
import useReveal, { reveal } from '../hooks/useReveal';
import { CONTACT } from '../data/site';

/*
  The endpoint, the field names and their order are unchanged from the previous
  form: anything reading these submissions downstream keeps working.

  What changed is the presentation: labels sit above their inputs, validation
  messages appear inline under the field they belong to, and a failed submit
  now renders an error in the form instead of firing window.alert().
*/
const DEMO_ENDPOINT = 'https://classroom-store-test.azurewebsites.net/v1/demo';

const EMPTY_FORM = { name: '', email: '', phone: '', school: '', message: '' };

const FIELDS = [
  { name: 'name', label: 'Full name', type: 'text', autoComplete: 'name', required: true },
  { name: 'email', label: 'Email address', type: 'email', autoComplete: 'email', required: true },
  { name: 'phone', label: 'Phone number', type: 'tel', autoComplete: 'tel', required: true },
  { name: 'school', label: 'School name', type: 'text', autoComplete: 'organization', required: true },
];

const DemoRequestForm = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ref, isVisible] = useReveal();

  const validate = () => {
    const next = {};

    if (!formData.name.trim()) next.name = 'Enter your full name';
    else if (formData.name.trim().length < 2) next.name = 'Enter at least 2 characters';

    if (!formData.email.trim()) next.email = 'Enter your email address';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) next.email = 'Enter a valid email address';

    if (!formData.phone.trim()) next.phone = 'Enter your phone number';
    else if (formData.phone.trim().length < 10) next.phone = 'Enter a valid phone number';

    if (!formData.school.trim()) next.school = 'Enter your school name';
    else if (formData.school.trim().length < 2) next.school = 'Enter at least 2 characters';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: '' }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    const honeypot = event.target.elements.website?.value || '';

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(DEMO_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The honeypot is only sent when something filled it, so real
        // submissions keep exactly the fields they always had.
        body: JSON.stringify(honeypot ? { ...formData, website: honeypot } : formData),
      });

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      setIsSubmitted(true);
      setFormData(EMPTY_FORM);
    } catch (error) {
      setSubmitError(
        `We could not send your request. Please try again, or call us on ${CONTACT.phoneDisplay}.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="demo-form" className="ab-section ab-section--tint ab-demo" ref={ref}>
      <div className="ab-shell ab-demo__grid">
        <div className={`ab-demo__intro ${reveal(isVisible)}`}>
          <h2 className="ab-h2">Book a demo</h2>
          <p className="ab-lead">
            Half an hour, your school’s actual numbers, and an honest answer about whether
            AtadaBoard fits. We reply within a working day.
          </p>

          <div className="ab-demo__call">
            <p className="ab-demo__callLabel">Rather talk now?</p>
            <a href={CONTACT.phoneHref} className="ab-demo__callNumber ab-num">
              <Phone size={18} strokeWidth={1.75} />
              {CONTACT.phoneDisplay}
            </a>
          </div>
        </div>

        <div className={`ab-demo__panel ${reveal(isVisible)}`} style={{ '--ab-reveal-delay': '90ms' }}>
          {isSubmitted ? (
            <div className="ab-demo__done" role="status">
              <span className="ab-demo__doneIcon">
                <CircleCheck size={30} strokeWidth={1.75} />
              </span>
              <h3 className="ab-h3">Your request is in</h3>
              <p className="ab-body">
                We have your details and will be in touch within a working day to arrange a time.
              </p>
              <div className="ab-demo__doneActions">
                <a href={CONTACT.phoneHref} className="ab-btn ab-btn--primary">
                  <Phone size={17} strokeWidth={1.75} />
                  Call us now
                </a>
                <button
                  type="button"
                  className="ab-btn ab-btn--secondary"
                  onClick={() => setIsSubmitted(false)}
                >
                  Send another request
                </button>
              </div>
            </div>
          ) : (
            <form className="ab-demo__form" onSubmit={handleSubmit} noValidate>
              {/* Honeypot: invisible to people and screen readers, filled by bots. */}
              <div className="ab-demo__hp" aria-hidden="true">
                <label htmlFor="demo-website">Website</label>
                <input id="demo-website" type="text" name="website" tabIndex={-1} autoComplete="off" />
              </div>
              {FIELDS.map((field) => (
                <div key={field.name} className="ab-demo__field">
                  <label htmlFor={`demo-${field.name}`}>{field.label}</label>
                  <input
                    id={`demo-${field.name}`}
                    name={field.name}
                    type={field.type}
                    autoComplete={field.autoComplete}
                    value={formData[field.name]}
                    onChange={handleChange}
                    className={errors[field.name] ? 'has-error' : ''}
                    aria-invalid={Boolean(errors[field.name])}
                    aria-describedby={errors[field.name] ? `demo-${field.name}-error` : undefined}
                    required={field.required}
                  />
                  {errors[field.name] && (
                    <p className="ab-demo__error" id={`demo-${field.name}-error`}>
                      {errors[field.name]}
                    </p>
                  )}
                </div>
              ))}

              <div className="ab-demo__field ab-demo__field--full">
                <label htmlFor="demo-message">
                  What should we focus on? <span>Optional</span>
                </label>
                <textarea
                  id="demo-message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Billing, attendance, parent communication, moving off paper records"
                />
              </div>

              {submitError && (
                <p className="ab-demo__submitError ab-demo__field--full" role="alert">
                  <CircleAlert size={17} strokeWidth={1.75} />
                  {submitError}
                </p>
              )}

              <div className="ab-demo__field--full ab-demo__submit">
                <button type="submit" className="ab-btn ab-btn--primary" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <LoaderCircle size={18} strokeWidth={2} className="ab-demo__spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      <Send size={17} strokeWidth={1.75} />
                      Book a demo
                    </>
                  )}
                </button>
                <p className="ab-demo__note">
                  We use these details to contact you about your demo, nothing else.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>

      <style jsx>{`
        .ab-demo__grid {
          display: grid;
          grid-template-columns: minmax(0, 0.82fr) minmax(0, 1.18fr);
          gap: clamp(2rem, 5vw, 4rem);
          align-items: start;
        }

        .ab-demo__intro .ab-lead { margin-top: 0.875rem; }

        .ab-demo__call {
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--ab-line);
        }

        .ab-demo__callLabel {
          font-size: 0.875rem;
          color: var(--ab-ink-3);
          margin-bottom: 0.5rem;
        }

        .ab-demo__callNumber {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: var(--ab-font-display);
          font-size: 1.25rem;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--ab-blue);
          text-decoration: none;
        }

        .ab-demo__callNumber:hover { color: var(--ab-blue-hover); }

        .ab-demo__callNumber:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 3px;
          border-radius: 6px;
        }

        .ab-demo__panel {
          padding: clamp(1.5rem, 3vw, 2.25rem);
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-card);
          background: var(--ab-paper);
          box-shadow: var(--ab-shadow-md);
        }

        .ab-demo__hp {
          position: absolute;
          left: -10000px;
          width: 1px;
          height: 1px;
          overflow: hidden;
        }

        .ab-demo__form {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1.125rem;
        }

        .ab-demo__field { display: flex; flex-direction: column; gap: 0.4375rem; }

        .ab-demo__field--full { grid-column: 1 / -1; }

        .ab-demo__field label {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--ab-ink);
        }

        .ab-demo__field label span {
          font-weight: 400;
          color: var(--ab-ink-3);
        }

        .ab-demo__field input,
        .ab-demo__field textarea {
          width: 100%;
          padding: 0.75rem 0.875rem;
          font-family: var(--ab-font-body);
          font-size: 1rem;
          color: var(--ab-ink);
          background: var(--ab-paper);
          border: 1px solid var(--ab-line-strong);
          border-radius: var(--ab-r-inner);
          transition: border-color 0.2s var(--ab-ease), box-shadow 0.2s var(--ab-ease);
        }

        .ab-demo__field textarea { resize: vertical; min-height: 104px; }

        /* Placeholder reads at 4.9:1 against the white field. */
        .ab-demo__field input::placeholder,
        .ab-demo__field textarea::placeholder { color: var(--ab-ink-3); }

        .ab-demo__field input:focus,
        .ab-demo__field textarea:focus {
          outline: none;
          border-color: var(--ab-blue);
          box-shadow: 0 0 0 3px var(--ab-blue-100);
        }

        .ab-demo__field input.has-error { border-color: #b3261e; }

        .ab-demo__error {
          font-size: 0.8125rem;
          color: #b3261e;
        }

        .ab-demo__submitError {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          padding: 0.75rem 0.875rem;
          font-size: 0.875rem;
          line-height: 1.5;
          color: #8c1d18;
          background: #fdf1f0;
          border: 1px solid #f2c6c2;
          border-radius: var(--ab-r-inner);
        }

        .ab-demo__submitError svg { flex-shrink: 0; margin-top: 1px; }

        .ab-demo__submit {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.875rem 1.25rem;
          margin-top: 0.25rem;
        }

        .ab-demo__note {
          font-size: 0.8125rem;
          color: var(--ab-ink-3);
          max-width: 34ch;
        }

        .ab-demo__spin { animation: abSpin 0.9s linear infinite; }

        @keyframes abSpin {
          to { transform: rotate(360deg); }
        }

        .ab-demo__done { text-align: left; }

        .ab-demo__doneIcon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 52px;
          height: 52px;
          margin-bottom: 1.125rem;
          border-radius: var(--ab-r-pill);
          background: var(--ab-blue-100);
          color: var(--ab-blue);
        }

        .ab-demo__done .ab-body { margin-top: 0.5rem; }

        .ab-demo__doneActions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 1.75rem;
        }

        @media (max-width: 940px) {
          .ab-demo__grid { grid-template-columns: minmax(0, 1fr); }
        }

        @media (max-width: 560px) {
          .ab-demo__form { grid-template-columns: minmax(0, 1fr); }
          .ab-demo__field--full { grid-column: auto; }
        }
      `}</style>
    </section>
  );
};

export default DemoRequestForm;
